import { assert, isDefined } from '@sama/js-core';
import { find } from '@sama/micro-dash';
import { filter, forOwn, some } from 'lodash';

export function strictForOwn<
  T extends StrictRecord<any, any>,
  Result extends void | boolean,
>(
  object: T,
  callback: (
    value: Exclude<T[keyof T], undefined>,
    key: string,
    obj: T,
  ) => Result,
) {
  return forOwn(object, (value, key, obj) => {
    assertDefined(value);
    return callback(value, key, obj);
  });
}

export function strictSome<
  TObject extends StrictRecord<any, any>,
  T = TObject[keyof TObject],
>(object: TObject, callback: (value: NonNullable<T>, key: string) => boolean) {
  return some(object, (value, key) => {
    assertDefined(value);
    return callback(value, key);
  });
}

export function strictFind<
  TObject extends StrictRecord<any, any>,
  T = TObject[keyof TObject],
>(object: TObject, callback: (value: NonNullable<T>) => boolean) {
  return find(object, (value) => {
    assertDefined(value);
    return callback(value);
  });
}

export function strictFilter<
  TObject extends StrictRecord<any, any>,
  T = TObject[keyof TObject],
>(
  object: TObject,
  callback: (value: NonNullable<T>, key: string) => boolean,
): Array<NonNullable<T>> {
  return filter(object, (value, key) => {
    assertDefined(value);
    return callback(value, key);
  });
}

export function strictValues<T>(object: StrictRecord<any, T>) {
  const values = Object.values(object);
  areAllValuesDefined(values);
  return values;
}

export function strictIterator<T, R>(iterator: (value: T, key: string) => R) {
  return (value: T | undefined, key: string) => {
    assertDefined(value);
    return iterator(value, key);
  };
}

export function getOrThrow<KeyType extends string | number | symbol, ValueType>(
  object: StrictRecord<KeyType, ValueType>,
  key: KeyType,
) {
  const value = object[key];
  assertDefined(value);
  return value;
}

export function assertDefined<T>(
  value: T | undefined,
): asserts value is Exclude<T, undefined> {
  assert(isDefined(value), 'value should be defined');
}

function areAllValuesDefined<T>(
  values: Array<T | undefined>,
): asserts values is T[] {
  values.forEach(assertDefined);
}

type NonNullable<T> = Exclude<T, undefined>;
